import { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'urql'
import { useReadContract } from 'wagmi'
import { env } from 'environments'
import { GetFeaturedSquigglesDocument } from './get-featured-squiggles.graphql'
import { getVaultsByWalletAddress } from '@/app/actions/user-vaults'
import type { ConditionHookResult } from '../_utils/createConditionHook'

// Relic contract addresses by network
const RELIC_CONTRACT_ADDRESSES = {
  mainnet: '0x9b917686DD68B68A780cB8Bf70aF46617A7b3f80',
  sepolia: '0xED1249f6B1C7eE7a13DBbcA3f1777EfDca4af481'
} as const

const currentNetwork = process.env.NEXT_PUBLIC_CURRENT_ENV === 'prod' ? 'mainnet' : 'sepolia'
const RELIC_CONTRACT_ADDRESS = RELIC_CONTRACT_ADDRESSES[currentNetwork]

// ABI for inscriptionByAddress function
const RELIC_ABI = [
  {
    inputs: [{ internalType: 'address', name: 'addr', type: 'address' }],
    name: 'inscriptionByAddress',
    outputs: [
      { internalType: 'bool', name: '', type: 'bool' },
      { internalType: 'uint256', name: '', type: 'uint256' }
    ],
    stateMutability: 'view',
    type: 'function'
  }
] as const

type FeaturedSquiggleOption = {
  value: string
  label: string
}

type FeaturedSquigglesParams = {
  ownerAddress: string
  projectId: string
}

type TokenData = {
  id: string
  token_id: string
  invocation: number
  hash: string
  project: {
    name: string | null
  }
}

type FeaturedSquigglesData = {
  tokens_metadata: TokenData[]
}

export function useFeaturedSquiggles(
  isActive: boolean,
  params: FeaturedSquigglesParams,
  selectedSquiggleValue?: string
): ConditionHookResult<FeaturedSquiggleOption[]> {
  const [vaultAddresses, setVaultAddresses] = useState<string[]>([])
  const [vaultLoading, setVaultLoading] = useState(false)
  const [vaultError, setVaultError] = useState<Error | null>(null)

  // Step 1: Check Relic contract inscription for token owner
  const {
    data: relicData,
    isLoading: relicLoading,
    error: relicError
  } = useReadContract({
    abi: RELIC_ABI,
    address: RELIC_CONTRACT_ADDRESS as `0x${string}`,
    functionName: 'inscriptionByAddress',
    args: [params.ownerAddress as `0x${string}`],
    chainId: env.chainId,
    query: {
      enabled: isActive && !!params.ownerAddress
    }
  })

  const hasRelicInscription = relicData?.[0] === true

  // Step 2: Fetch vault addresses from services API
  useEffect(() => {
    if (!isActive || !params.ownerAddress) return

    const fetchVaults = async () => {
      setVaultLoading(true)
      setVaultError(null)

      try {
        const data = await getVaultsByWalletAddress(params.ownerAddress)
        const vaults = data.linkedVaults?.map((vault) => vault.vault.toLowerCase()) || []
        setVaultAddresses(vaults)
      } catch (error) {
        console.error('Error fetching vault addresses:', error)
        setVaultError(error as Error)
        setVaultAddresses([]) // Continue with just owner address
      } finally {
        setVaultLoading(false)
      }
    }

    fetchVaults()
  }, [isActive, params.ownerAddress])

  // Step 3: Query GraphQL for tokens owned by owner + vaults
  const allAddresses = [params.ownerAddress, ...vaultAddresses]

  const [result] = useQuery<FeaturedSquigglesData>({
    query: GetFeaturedSquigglesDocument as any, // Will be properly typed after GraphQL codegen
    variables: {
      ownerAddresses: allAddresses,
      projectId: params.projectId
    },
    pause: !isActive || vaultLoading || relicLoading // Wait for vaults and relic to load
  })

  const { data, fetching, error } = result

  // Combine loading states and errors
  const isLoading = vaultLoading || fetching || relicLoading
  const combinedError = vaultError || error || relicError

  // Normalize data
  const normalizedData = (() => {
    // always include the default squiggle
    const result = [
      {
        value: '1981',
        label: 'Default: Chromie Squiggle #1981'
      }
    ]

    // Add Relic squiggle if owner has inscription
    if (hasRelicInscription) {
      result.push({
        value: '9998',
        label: 'Relic: Chromie Squiggle #9998'
      })
    }

    // map from valid squiggles to labeled token numbers and owned tokenId values
    if (!data?.tokens_metadata?.length) return result

    return result.concat(
      data.tokens_metadata.map((token: TokenData) => ({
        value: token.invocation.toString(),
        label: `Chromie Squiggle #${token.invocation}`
      }))
    )
  })()

  // Generate supplemental params based on selected squiggle
  const supplementalParams = useMemo(() => {
    const params: Record<string, string> = {}

    if (selectedSquiggleValue === '1981') {
      params['Splash Screen Squiggle Hash'] = '0xe80706d684a840a256b04a97711e21a8ee587fd948f14f212f18cbf60958d29c'
    } else if (selectedSquiggleValue === '9998') {
      params['Splash Screen Squiggle Hash'] = '0x1bb4bf64d751340b093e83816c1044ff0534e6fec12f36e6b64a1494741ea815'
    } else if (selectedSquiggleValue && selectedSquiggleValue !== '1981' && data?.tokens_metadata) {
      // Find the token data for the selected squiggle
      const selectedToken = data.tokens_metadata.find((token) => token.invocation.toString() === selectedSquiggleValue)

      if (selectedToken?.hash) {
        params['Splash Screen Squiggle Hash'] = selectedToken.hash
      }
    }

    return Object.keys(params).length > 0 ? params : undefined
  }, [selectedSquiggleValue, data?.tokens_metadata])

  return {
    data: normalizedData,
    fetching: isLoading,
    error: combinedError,
    supplementalParams
  }
}
